import { useState } from 'react'
import { FieldArrayWithId, FieldPath, UseFormGetValues, UseFormTrigger } from 'react-hook-form'
import { CourseForm as CourseFormType } from '@/kmsApp/types/forms/course-form'

type UseCourseWizardParams = {
  /** Le passager est saisi à la main quand personne n'est connecté. */
  hasClientStep: boolean
  fields: FieldArrayWithId<CourseFormType, 'arrets', 'id'>[]
  trigger: UseFormTrigger<CourseFormType>
  getValues: UseFormGetValues<CourseFormType>
  /**
   * Vrai quand un trajet préconfiguré est retenu : les champs d'arrêt sont
   * masqués et vides, les valider bloquerait l'étape pour rien.
   */
  skipStopValidation?: boolean
}

/**
 * Navigation du formulaire de réservation en plusieurs étapes.
 *
 * Chaque étape ne valide que ses propres champs : on ne peut avancer qu'une
 * fois l'étape courante correcte, sans pour autant afficher les erreurs des
 * étapes suivantes.
 */
export const useCourseWizard = ({
  hasClientStep,
  fields,
  trigger,
  getValues,
  skipStopValidation = false,
}: UseCourseWizardParams) => {
  const [currentStep, setCurrentStep] = useState(1)

  const stopStepNumber = 1
  const tripStepNumber = 2
  const clientStepNumber = hasClientStep ? 3 : -1 // Valide uniquement si déconnecté
  const totalSteps = 2 + (hasClientStep ? 1 : 0) + 1
  const recapStepNumber = totalSteps

  const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1))

  /** Champs à valider pour quitter l'étape courante. */
  const getFieldsToValidate = (): FieldPath<CourseFormType>[] | undefined => {
    if (currentStep === stopStepNumber) {
      const stopFields: FieldPath<CourseFormType>[] = []

      if (!skipStopValidation) {
        fields.forEach((_, index) => {
          const isNumDetected = getValues(`arrets.${index}.adresse.isNumDetected`)
          const numero = getValues(`arrets.${index}.adresse.numero`)
          // La rue porte la validation "au moins un champ d'adresse rempli" :
          // c'est elle qui garantit qu'une adresse a bien été sélectionnée.
          stopFields.push(`arrets.${index}.adresse.rue`)
          if (!isNumDetected && !numero?.trim()) {
            stopFields.push(`arrets.${index}.adresse.numero`)
          }
        })
      }

      return [
        ...stopFields,
        'dateCourse',
        ...(getValues('avecTrajetRetour') ? (['dateTrajetRetour'] as const) : []),
      ] as FieldPath<CourseFormType>[]
    }

    if (currentStep === tripStepNumber) {
      return ['idService', 'nombresPassagers', 'nombresValises', 'nombresSacs', 'siegesEnfants']
    }

    if (hasClientStep && currentStep === clientStepNumber) {
      return ['passager.nom', 'passager.prenom', 'passager.email', 'passager.phone']
    }

    return undefined
  }

  /**
   * Règles qui ne se déduisent pas d'un champ isolé, donc hors de portée de
   * react-hook-form : un passager tiers doit être identifié, et le retour doit
   * être postérieur à l'aller.
   */
  const passesCrossFieldRules = (): boolean => {
    if (
      currentStep === tripStepNumber &&
      getValues('reservedFor') === 'external' &&
      !getValues('idContactPassager') &&
      !getValues('passager.nom')
    ) {
      return false
    }

    if (currentStep === stopStepNumber && getValues('avecTrajetRetour')) {
      const dateCourse = getValues('dateCourse')
      const dateTrajetRetour = getValues('dateTrajetRetour')

      if (dateCourse && dateTrajetRetour && new Date(dateCourse) >= new Date(dateTrajetRetour)) {
        return false
      }
    }

    return true
  }

  const goToNextStep = async () => {
    const fieldsToValidate = getFieldsToValidate()
    const isValid = fieldsToValidate ? await trigger(fieldsToValidate) : true

    if (isValid && passesCrossFieldRules()) {
      setCurrentStep((prev) => Math.min(prev + 1, totalSteps))
    }
  }

  return {
    currentStep,
    totalSteps,
    stopStepNumber,
    tripStepNumber,
    clientStepNumber,
    recapStepNumber,
    /** Largeur d'une étape dans le carrousel horizontal, en pourcentage. */
    stepWidthPercentage: 100 / totalSteps,
    prevStep,
    goToNextStep,
  }
}
